import axios from "axios";
import DOMPurify from "isomorphic-dompurify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getApiUrl } from "../../utils/apiUrls";
import { apiErrorHandler } from "../../utils/errorHandler";
import { openSnackBar } from "../../redux/actions/snackbar.actions";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import CustomButtonLoader from "../global/CustomButtonLoader.component";
import { ReactComponent as Clipboard } from '../../assets/images/tasks/Clipboard-Pink.svg';
import CustomCheckbox from '../global/CustomCheckbox.component';
import { sanitiseInput } from "../../utils/helperFn";

const PendingConsent = ({t,data, loginHandler, handleBackToLogin}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const refId = searchParams.get('refId');

    const [crfData, setCrfData] = useState({
        data: null,
        isLoading: true,
        isSaveBtnLoading: false,
    });

    useEffect(() => {
        getCandidateConsentDetails();
    }, [data?.refId]);

    const getCandidateConsentDetails = async () => {
      try {
        const res = await axios.post(`${getApiUrl('getCandidateAllPreloginConsentDetails')}`, {
            title: null,
            description: null,
            langId: null,
            consentDetailsDtoList: null,
            showConsentPage: null,
            email: data?.username,
            defaultConsent: null,
        });

        if(res.data) {
          setCrfData({
            ...crfData,
            ...res.data,
            data: res.data?.consentDetailsDtoList,
            isLoading: false,
            showCrf: res.data.showConsentPage,
            hasGetConsentDetailsRun: true
          });
        }

      } catch (error) {
        const errObj = apiErrorHandler(error);
        
        dispatch(openSnackBar({
            msg: t(errObj.statusText),
            type: 'error'
        }))
      }
    }

    const handleSaveCrfConsent = async () => {
        try {
            //Check if all mandatory consents are checked
            const allMandatoryChecked = crfData.data.every(item => {
                if(item.mandatory && item.checkbox) {
                    return item.checked === true;
                }
                return true;
            });

            if(!allMandatoryChecked) {
                // Add error message for unchecked mandatory consents
                const updatedFormVal = crfData.data.map(item => {
                    if(item.mandatory && item.checkbox && !item.checked) {
                        return {
                            ...item,
                            msg: "this_consent_is_mandatory"
                        }
                    }
                    return item;
                });
                setCrfData({...crfData, data: updatedFormVal});

                dispatch(openSnackBar({
                    msg: t("accept_all_mandatory_consents_to_proceed"),
                    type: "error"
                }))
                return;
            }

            setCrfData({
                ...crfData,
                isSaveBtnLoading: true
            })

            const dataToSend = {
                title: crfData?.title,
                description: crfData?.description,
                langId: crfData?.langId,
                consentDetailsDtoList: crfData.data.map(item => ({
                    orderId: item.orderId,
                    mandatory: item.mandatory,
                    enable: item.enable,
                    consentMessage: item.consentMessage,
                    modified: item.modified,
                    id: item.id,
                    checked: !item.checkbox ? true : item.checked,
                    checkbox: item.checkbox,
                    checkedAndSubmittedConsentIds: item.checkedAndSubmittedConsentIds,
                    unCheckedAndSubmittedConsentIds: item.unCheckedAndSubmittedConsentIds,
                    deleteDisable: item.deleteDisable,
                    mandatoryChange: item.mandatoryChange
                })),
                showConsentPage: crfData?.showConsentPage,
                email: data?.username,
                defaultConsent: crfData?.defaultConsent,
            }

            // console.log("dataToSend", dataToSend);
            // setIsLoading(false);
            // return;

            //API call to save consent details
            const res = await axios.post(`${getApiUrl('saveCandidatePreloginConsentDetails')}`, dataToSend);

            if(res.data) {
               setCrfData({
                    ...crfData,
                    isSaveBtnLoading: false
                }) 

                loginHandler(data, 1)
            }
        } catch (error) {
            setCrfData({
                ...crfData,
                isSaveBtnLoading: false
            })
            setCrfData({
                ...crfData,
                isSaveBtnLoading: false
            })
            const errObj = apiErrorHandler(error);

            dispatch(openSnackBar({
                msg: t(errObj.statusText),
                type: 'error'
            }))
        }
    }

    const handleConsentFieldChange = (index) => {
        setCrfData((prevState) => {
            const updatedCrfData = prevState.data.map((item, idx) => {
                if(idx === index) {
                    return {
                        ...item,
                        checked: !item.checked,
                        ...(item?.msg ? {msg: null} : {})
                    }
                }
                return item;
            });
            return {...prevState, data: updatedCrfData};
        });
    }

    return <div>
        <div className="cm-crf-inner-wrapper bg-white p-5">
            <h4 className='mb-4 pb-4 border-bottom border-blue-gray-200 text-blue-800 d-flex align-items-start cm-crf-heading-wrapper text-break'><Clipboard /> <span className='ps-2'>{t("pending_consent_title")}</span></h4>
            {
                crfData.data && crfData.data.length > 0 ? crfData.data.map((item, index) => (
                    <div key={"crf-field-" + index} className='mb-4 d-flex align-items-start'>
                        {item.checkbox && <div className='mt-1'>
                            <CustomCheckbox
                                isChecked={item.checked}
                                handleCheckboxChange={(e) => handleConsentFieldChange(index)}
                                checkboxId={"mandatory-checkbox-" + index}
                            />
                        </div>}
                        <div className={`text-break ${item.checkbox ? "ps-10" : ""}`}>
                            <div dangerouslySetInnerHTML={{ __html: sanitiseInput(item.consentMessage) }} className='cm-gen-styles text-blue-800'></div>
                            {item.msg && <span className='text-danger text-break cm-xs-txt fw-medium pt-2 d-inline-block'>{t(item.msg)}</span>}
                        </div>
                    </div>
                )) : <div>{t("no_consent_details_available")}</div>
            }

            <div className='border-top border-blue-gray-200 pt-32 mt-32 d-flex align-items-center'>
                <CustomButtonLoader
                    showLoadingState={crfData.isSaveBtnLoading}
                    colorTheme="blue"
                    reverseIconDirection={true}
                    buttonLabel={t("accept_and_continue")}
                    buttonStyle={"py-12 px-3"}
                    handleOnClick={handleSaveCrfConsent}
                />
                <CustomButtonLoader 
                    colorTheme="white"
                    reverseIconDirection={true}
                    buttonLabel={t("back_to_login")}
                    buttonStyle={"py-12 px-3 ms-3"}
                    handleOnClick={handleBackToLogin}
                />
            </div>
        </div>
    </div>;

}

export default PendingConsent;